// ═══════════════════════════════════════════════════════════════
// DEBRIS — Personnel Roster
// Import and display 800+ personnel from ERC rosters (XLSX)
// Data persisted as JSON in debrisDocuments table
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Modal, Stack, SimpleGrid, Alert,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconUpload, IconRefresh,
  IconAlertTriangle, IconPencil, IconPlus,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { fetchRecords, createRecord, updateRecord } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';

const FDoc = FIELDS.debrisDocuments;

// ── Types ──

export interface PersonnelRecord {
  name: string;
  employeeId: string;
  erc: string;            // ERC-1, ERC-2, ERC-3
  department: string;
  role: string;
  shift: string;          // A or B
  phoneCity: string;
  phonePersonal: string;
  fleetNumber: string;
  fringeRate: number;     // benefit/fringe rate as a percentage, e.g. 35.5
  hourlyRate?: number;    // standard hourly pay rate
  certs: PersonnelCerts;
}

export interface PersonnelCerts {
  chainsaw4hr?: boolean;
  chainsaw8hr?: boolean;
  nims100?: boolean;
  nims200?: boolean;
  nims300?: boolean;
  nims400?: boolean;
  nims700?: boolean;
  nims800?: boolean;
  crisisTrack?: boolean;
  webEOC?: boolean;
  alertTampa?: boolean;
  pushCrewLead?: boolean;
  operator?: boolean;
  safetyObserver?: boolean;
  wexFuelCard?: boolean;
}

const CERT_LABELS: Record<keyof PersonnelCerts, string> = {
  chainsaw4hr: 'Chainsaw 4hr',
  chainsaw8hr: 'Chainsaw 8hr',
  nims100: 'NIMS 100',
  nims200: 'NIMS 200',
  nims300: 'NIMS 300',
  nims400: 'NIMS 400',
  nims700: 'NIMS 700',
  nims800: 'NIMS 800',
  crisisTrack: 'Crisis Track',
  webEOC: 'WebEOC',
  alertTampa: 'Alert Tampa',
  pushCrewLead: 'Push Crew Lead',
  operator: 'Operator',
  safetyObserver: 'Safety Observer',
  wexFuelCard: 'WEX Fuel Card',
};

const ERC_FORM_OPTIONS = [
  { value: '', label: '— None —' },
  { value: 'ERC-1', label: 'ERC-1' },
  { value: 'ERC-2', label: 'ERC-2' },
  { value: 'ERC-3', label: 'ERC-3' },
];

const ERC_FILTER_OPTIONS = [
  { value: '', label: 'All ERCs' },
  { value: 'ERC-1', label: 'ERC-1' },
  { value: 'ERC-2', label: 'ERC-2' },
  { value: 'ERC-3', label: 'ERC-3' },
];

const SHIFT_FORM_OPTIONS = [
  { value: '', label: '— None —' },
  { value: 'A', label: 'A-Shift' },
  { value: 'B', label: 'B-Shift' },
];

const SHIFT_FILTER_OPTIONS = [
  { value: '', label: 'All Shifts' },
  { value: 'A', label: 'A-Shift' },
  { value: 'B', label: 'B-Shift' },
];

const BLANK = (): PersonnelRecord => ({
  name: '', employeeId: '', erc: '', department: '', role: '',
  shift: '', phoneCity: '', phonePersonal: '', fleetNumber: '',
  fringeRate: 37, certs: {},
});

// ── Roster Storage (debrisDocuments) ──

const ROSTER_DOC_TYPE = 'roster_data';

async function loadRosterFromDB(): Promise<{ id: number | null; data: PersonnelRecord[] }> {
  try {
    const res = await fetchRecords<Record<string, unknown>>(TABLES.debrisDocuments, { limit: 50 });
    const doc = res.records.find(r => r[FDoc.documentType] === ROSTER_DOC_TYPE);
    if (!doc) return { id: null, data: [] };
    const notesRaw = String(doc[FDoc.notes] || '');
    try {
      const data = JSON.parse(notesRaw) as PersonnelRecord[];
      return { id: doc.record_id as number, data };
    } catch { return { id: doc.record_id as number, data: [] }; }
  } catch (e) {
    console.error('[PersonnelRoster] loadRosterFromDB', e);
    return { id: null, data: [] };
  }
}

async function saveRosterToDB(existingId: number | null, data: PersonnelRecord[]): Promise<number> {
  const json = JSON.stringify(data);
  const payload: Record<string, unknown> = {
    [FDoc.title]: 'ERC Personnel Roster Data',
    [FDoc.documentType]: ROSTER_DOC_TYPE,
    [FDoc.notes]: json,
  };
  if (existingId) {
    await updateRecord(TABLES.debrisDocuments, existingId, payload);
    return existingId;
  } else {
    const r = await createRecord<Record<string, unknown>>(TABLES.debrisDocuments, payload);
    return (r as any).record_id as number;
  }
}

// ── XLSX Parsing ──

function parseRosterXLSX(workbook: any, fileName: string): { erc: string; records: PersonnelRecord[] } {
  let erc = 'ERC-1';
  const fnLower = fileName.toLowerCase();
  if (fnLower.includes('erc-2') || fnLower.includes('erc 2') || fnLower.includes('erc2')) erc = 'ERC-2';
  else if (fnLower.includes('erc-3') || fnLower.includes('erc 3') || fnLower.includes('erc3')) erc = 'ERC-3';

  const sheetNames = workbook.SheetNames as string[];
  const voltronSheet = sheetNames.find((n: string) => n.toLowerCase().includes('voltron'));
  const sheetName = voltronSheet || sheetNames[0];
  const sheet = workbook.Sheets[sheetName];

  const XLSX = (window as any).__XLSX__;
  const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

  const records: PersonnelRecord[] = [];

  let headerIdx = -1;
  for (let i = 0; i < Math.min(rows.length, 10); i++) {
    const row = rows[i];
    if (!row) continue;
    const joined = row.map((c: any) => String(c).toLowerCase()).join(' ');
    if (joined.includes('function') || joined.includes('name') || joined.includes('employee')) {
      headerIdx = i;
      break;
    }
  }
  if (headerIdx === -1) headerIdx = 0;

  for (let i = headerIdx + 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.length === 0) continue;

    const role = String(row[0] || '').trim();
    const isHeader = role.toUpperCase() === role && role.length > 3 && !role.includes(',');
    const skipWords = ['shift', 'function', 'section', 'a-team', 'b-team', 'legend', 'information'];
    const isSkip = skipWords.some(w => role.toLowerCase().includes(w));

    const aName = String(row[1] || '').trim();
    if (aName && aName.length > 2 && !isSkip && !aName.toLowerCase().includes('a-shift') && !aName.toLowerCase().includes('b-shift')) {
      const looksLikeName = aName.includes(',') || aName.includes('-') || /[A-Z][a-z]/.test(aName);
      if (looksLikeName && !isHeader) {
        const parsed = parsePersonFromRow(aName, role, row, 'A', erc, 4);
        if (parsed) records.push(parsed);
      }
    }

    if (row.length > 9) {
      const bName = String(row[9] || '').trim();
      if (bName && bName.length > 2 && !isSkip && !bName.toLowerCase().includes('a-shift') && !bName.toLowerCase().includes('b-shift')) {
        const looksLikeName = bName.includes(',') || bName.includes('-') || /[A-Z][a-z]/.test(bName);
        if (looksLikeName && !isHeader) {
          const parsed = parsePersonFromRow(bName, role, row, 'B', erc, 12);
          if (parsed) records.push(parsed);
        }
      }
    }
  }

  return { erc, records };
}

function parsePersonFromRow(
  nameRaw: string, role: string, row: any[], shift: string, erc: string, phoneOffset: number,
): PersonnelRecord | null {
  if (!nameRaw || nameRaw.length < 2) return null;

  let name = nameRaw;
  let employeeId = '';
  const idMatch = nameRaw.match(/^(.*?)\s*-\s*(\d+)\s*$/);
  if (idMatch) {
    name = idMatch[1].trim();
    employeeId = idMatch[2];
  }
  if (name.includes(',')) {
    const parts = name.split(',').map(p => p.trim());
    name = `${parts[1]} ${parts[0]}`;
  }

  const phoneCity = String(row[phoneOffset] || '').trim();
  const phonePersonal = String(row[phoneOffset + 1] || '').trim();
  const fleetNumber = String(row[phoneOffset + 2] || '').trim();

  return {
    name, employeeId, erc,
    department: '',
    role: role || '',
    shift, phoneCity, phonePersonal, fleetNumber,
    fringeRate: 0,
    certs: {},
  };
}

// ── Component ──

export default function PersonnelRoster() {
  const [roster, setRoster] = useState<PersonnelRecord[]>([]);
  const [rosterDocId, setRosterDocId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [ercFilter, setErcFilter] = useState('');
  const [shiftFilter, setShiftFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  // Edit / Add
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null); // null = add new
  const [editForm, setEditForm] = useState<PersonnelRecord>(BLANK());
  const [saving, setSaving] = useState(false);

  // Import
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importPreview, setImportPreview] = useState<{ erc: string; records: PersonnelRecord[] } | null>(null);
  const [importing, setImporting] = useState(false);
  const [xlsxLoaded, setXlsxLoaded] = useState(false);

  // Sort
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { id, data } = await loadRosterFromDB();
      // Backfill any record missing a fringe rate to the standard 37%
      const needsMigration = data.some(r => !r.fringeRate);
      const migrated = needsMigration ? data.map(r => ({ ...r, fringeRate: r.fringeRate || 37 })) : data;
      if (needsMigration) {
        const newId = await saveRosterToDB(id, migrated);
        setRosterDocId(newId);
      } else {
        setRosterDocId(id);
      }
      setRoster(migrated);
      setLoading(false);
    })();
  }, []);

  // ── Edit / Add ──

  const openAdd = () => {
    setEditForm(BLANK());
    setEditIndex(null);
    setEditModalOpen(true);
  };

  const openEdit = (p: PersonnelRecord, rosterIdx: number) => {
    setEditForm({ fringeRate: 0, certs: {}, ...p });
    setEditIndex(rosterIdx);
    setEditModalOpen(true);
  };

  const savePerson = async () => {
    if (!editForm.name.trim()) {
      notifications.show({ message: 'Name is required', color: 'red' });
      return;
    }
    setSaving(true);
    try {
      let updated: PersonnelRecord[];
      if (editIndex === null) {
        updated = [...roster, editForm];
      } else {
        updated = roster.map((r, i) => i === editIndex ? editForm : r);
      }
      const newId = await saveRosterToDB(rosterDocId, updated);
      setRosterDocId(newId);
      setRoster(updated);
      setEditModalOpen(false);
      notifications.show({
        title: editIndex === null ? 'Added' : 'Updated',
        message: `${editForm.name} ${editIndex === null ? 'added to' : 'updated in'} roster`,
        color: 'green',
      });
    } catch (e) {
      notifications.show({ title: 'Save Failed', message: String(e), color: 'red' });
    }
    setSaving(false);
  };

  const setField = (k: keyof PersonnelRecord, v: unknown) =>
    setEditForm(f => ({ ...f, [k]: v }));

  // ── XLSX Import ──

  const loadXLSX = useCallback(async () => {
    if ((window as any).__XLSX__) { setXlsxLoaded(true); return; }
    const script = document.createElement('script');
    script.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
    script.onload = () => {
      (window as any).__XLSX__ = (window as any).XLSX;
      setXlsxLoaded(true);
    };
    document.head.appendChild(script);
  }, []);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!xlsxLoaded) await loadXLSX();
    await new Promise(r => setTimeout(r, 100));
    const XLSX = (window as any).__XLSX__;
    if (!XLSX) { notifications.show({ title: 'Error', message: 'XLSX library not loaded', color: 'red' }); return; }
    const arrayBuf = await file.arrayBuffer();
    const wb = XLSX.read(arrayBuf, { type: 'array' });
    const result = parseRosterXLSX(wb, file.name);
    setImportPreview(result);
  };

  const confirmImport = async () => {
    if (!importPreview) return;
    setImporting(true);
    try {
      const otherRecords = roster.filter(r => r.erc !== importPreview.erc);
      const merged = [...otherRecords, ...importPreview.records];
      const newId = await saveRosterToDB(rosterDocId, merged);
      setRosterDocId(newId);
      setRoster(merged);
      setImportModalOpen(false);
      setImportPreview(null);
      notifications.show({
        title: 'Import Complete',
        message: `Imported ${importPreview.records.length} personnel from ${importPreview.erc} roster`,
        color: 'green',
      });
    } catch (e) {
      notifications.show({ title: 'Import Failed', message: String(e), color: 'red' });
    }
    setImporting(false);
  };

  // ── Filters / Sort ──

  const roles = useMemo(() => {
    const s = new Set(roster.map(r => r.role).filter(Boolean));
    return Array.from(s).sort();
  }, [roster]);

  // filtered entries carry their original roster index for edit targeting
  const filtered = useMemo(() => {
    let list = roster.map((p, i) => ({ ...p, _rosterIdx: i }));
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.employeeId.includes(q) ||
        r.role.toLowerCase().includes(q) ||
        r.department.toLowerCase().includes(q)
      );
    }
    if (ercFilter) list = list.filter(r => r.erc === ercFilter);
    if (shiftFilter) list = list.filter(r => r.shift === shiftFilter);
    if (roleFilter) list = list.filter(r => r.role === roleFilter);

    return [...list].sort((a, b) => {
      if (!sortField) return a.name.localeCompare(b.name);
      let va = '', vb = '';
      if (sortField === 'name') { va = a.name; vb = b.name; }
      else if (sortField === 'employeeId') { va = a.employeeId; vb = b.employeeId; }
      else if (sortField === 'erc') { va = a.erc; vb = b.erc; }
      else if (sortField === 'department') { va = a.department; vb = b.department; }
      else if (sortField === 'role') { va = a.role; vb = b.role; }
      else if (sortField === 'shift') { va = a.shift; vb = b.shift; }
      else if (sortField === 'fringeRate') { va = String(a.fringeRate ?? 0); vb = String(b.fringeRate ?? 0); }
      const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? n : -n;
    });
  }, [roster, search, ercFilter, shiftFilter, roleFilter, sortField, sortDir]);

  // ── Stats ──

  const ercCounts = useMemo(() => {
    const m: Record<string, number> = {};
    roster.forEach(r => { m[r.erc] = (m[r.erc] || 0) + 1; });
    return m;
  }, [roster]);

  const shiftCounts = useMemo(() => {
    const m: Record<string, number> = {};
    roster.forEach(r => { m[r.shift] = (m[r.shift] || 0) + 1; });
    return m;
  }, [roster]);

  const SortTh = ({ field, label, style: s }: { field: string; label: string; style?: React.CSSProperties }) => (
    <th
      onClick={() => toggleSort(field)}
      style={{
        cursor: 'pointer', userSelect: 'none', padding: '10px 12px', fontSize: 12,
        fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em',
        color: sortField === field ? '#4B3FFF' : '#64748b',
        borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap', ...s,
      }}
    >
      {label} {sortField === field ? (sortDir === 'desc' ? '↓' : '↑') : <span style={{ opacity: 0.25 }}>↑</span>}
    </th>
  );

  if (loading) return <Text style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Loading roster...</Text>;

  return (
    <Stack gap={12}>
      <Group style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Title order={3} style={{ margin: 0 }}>Personnel Roster</Title>
        <Group gap="sm">
          <Button variant="subtle" size="compact-sm" onClick={async () => {
            setLoading(true);
            const { id, data } = await loadRosterFromDB();
            setRosterDocId(id); setRoster(data); setLoading(false);
          }}>
            <IconRefresh size={14} />
          </Button>
          <Button variant="light" size="compact-sm" onClick={openAdd}>
            <IconPlus size={14} style={{ marginRight: 4 }} /> Add Person
          </Button>
          <Button size="compact-sm" onClick={() => { loadXLSX(); setImportModalOpen(true); setImportPreview(null); }}>
            <IconUpload size={14} style={{ marginRight: 4 }} /> Import Roster
          </Button>
        </Group>
      </Group>

      {/* Stats */}
      <SimpleGrid cols={6} style={{ gap: 12 }}>
        {[
          { label: 'Total Personnel', value: roster.length, color: '#4B3FFF' },
          { label: 'ERC-1', value: ercCounts['ERC-1'] || 0, color: '#037730' },
          { label: 'ERC-2', value: ercCounts['ERC-2'] || 0, color: '#d97706' },
          { label: 'ERC-3', value: ercCounts['ERC-3'] || 0, color: '#7c3aed' },
          { label: 'A-Shift', value: shiftCounts['A'] || 0, color: '#0d9488' },
          { label: 'B-Shift', value: shiftCounts['B'] || 0, color: '#dc2626' },
        ].map((s, i) => (
          <Paper key={i} style={{
            padding: 12, borderRadius: 12, border: '1px solid #e8eaf0',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)', textAlign: 'center',
          }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
            <Text size="sm" style={{ color: '#64748b' }}>{s.label}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {roster.length === 0 && (
        <Alert color="blue" icon={<IconAlertTriangle size={16} />}>
          No personnel data loaded. Click "Add Person" to manually add someone, or "Import Roster" to upload an ERC roster XLSX.
        </Alert>
      )}

      {/* Filters */}
      <Group gap="sm">
        <TextInput
          placeholder="Search by name, ID, role..."
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          style={{ flex: 1, maxWidth: 300 }}
          leftSection={<IconSearch size={14} />}
        />
        <Select data={ERC_FILTER_OPTIONS} value={ercFilter}
          onChange={(v: string | null) => setErcFilter(v || '')} style={{ width: 120 }} placeholder="ERC" />
        <Select data={SHIFT_FILTER_OPTIONS} value={shiftFilter}
          onChange={(v: string | null) => setShiftFilter(v || '')} style={{ width: 120 }} placeholder="Shift" />
        <Select
          data={[{ value: '', label: 'All Roles' }, ...roles.map(r => ({ value: r, label: r }))]}
          value={roleFilter}
          onChange={(v: string | null) => setRoleFilter(v || '')}
          style={{ width: 160 }}
          placeholder="Role"
        />
      </Group>

      {/* Table */}
      {roster.length > 0 && (
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 420px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
              <tr>
                <SortTh field="name" label="Name" />
                <SortTh field="employeeId" label="Employee ID" />
                <SortTh field="erc" label="ERC" />
                <SortTh field="department" label="Department" />
                <SortTh field="role" label="Role" />
                <SortTh field="shift" label="Shift" />
                <SortTh field="fringeRate" label="Fringe %" />
                <th style={{
                  padding: '10px 12px', fontSize: 12, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b',
                  borderBottom: '2px solid #e2e8f0', width: 40,
                }} />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>
                  No personnel match filters
                </td></tr>
              )}
              {filtered.map((p, idx) => (
                <tr
                  key={`${p.employeeId || p.name}-${idx}`}
                  style={{ borderBottom: '1px solid #f1f5f9' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                >
                  <td style={{ padding: '10px 12px', fontWeight: 600 }}>{p.name}</td>
                  <td style={{ padding: '10px 12px', fontVariantNumeric: 'tabular-nums' }}>{p.employeeId || '—'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    {p.erc ? (
                      <Badge size="sm" color={p.erc === 'ERC-1' ? 'green' : p.erc === 'ERC-2' ? 'orange' : 'violet'}>
                        {p.erc}
                      </Badge>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#64748b' }}>{p.department || '—'}</td>
                  <td style={{ padding: '10px 12px' }}>{p.role || '—'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    {p.shift
                      ? <Badge size="sm" color={p.shift === 'A' ? 'blue' : 'red'}>{p.shift}-Shift</Badge>
                      : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', fontVariantNumeric: 'tabular-nums' }}>
                    {p.fringeRate ? `${p.fringeRate}%` : '—'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <IconPencil
                      size={14}
                      style={{ cursor: 'pointer', color: '#4B3FFF' }}
                      onClick={() => openEdit(p, p._rosterIdx)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit Modal */}
      <Modal
        opened={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={editIndex === null ? 'Add Person' : 'Edit Person'}
        size="lg"
      >
        <Stack gap={16}>
          {/* Identity */}
          <SimpleGrid cols={2} style={{ gap: 12 }}>
            <TextInput
              label="Name"
              placeholder="First Last"
              value={editForm.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('name', e.target.value)}
              required
            />
            <TextInput
              label="Employee ID"
              placeholder="12345"
              value={editForm.employeeId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('employeeId', e.target.value)}
            />
            <TextInput
              label="Department"
              placeholder="Public Works"
              value={editForm.department}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('department', e.target.value)}
            />
            <TextInput
              label="Role / Classification"
              placeholder="Equipment Operator"
              value={editForm.role}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('role', e.target.value)}
            />
            <Select
              label="ERC District"
              data={ERC_FORM_OPTIONS}
              value={editForm.erc}
              onChange={(v: string | null) => setField('erc', v || '')}
            />
            <Select
              label="Shift"
              data={SHIFT_FORM_OPTIONS}
              value={editForm.shift}
              onChange={(v: string | null) => setField('shift', v || '')}
            />
          </SimpleGrid>

          {/* Rates + fleet */}
          <SimpleGrid cols={3} style={{ gap: 12 }}>
            <TextInput
              label="Hourly Rate ($)"
              placeholder="28.50"
              type="number"
              value={editForm.hourlyRate ? String(editForm.hourlyRate) : ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setField('hourlyRate', parseFloat(e.target.value) || undefined)
              }
            />
            <TextInput
              label="Fringe / Benefits Rate %"
              placeholder="35.5"
              type="number"
              value={editForm.fringeRate ? String(editForm.fringeRate) : ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setField('fringeRate', parseFloat(e.target.value) || 0)
              }
            />
            <TextInput
              label="Fleet / Vehicle #"
              placeholder="T-42"
              value={editForm.fleetNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('fleetNumber', e.target.value)}
            />
          </SimpleGrid>

          <Group style={{ justifyContent: 'flex-end' }}>
            <Button variant="subtle" onClick={() => setEditModalOpen(false)}>Cancel</Button>
            <Button onClick={savePerson} loading={saving}>
              {editIndex === null ? 'Add to Roster' : 'Save Changes'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Import Modal */}
      <Modal opened={importModalOpen} onClose={() => setImportModalOpen(false)} title="Import ERC Roster" size="lg">
        <Stack gap={16}>
          <Text size="sm" style={{ color: '#64748b' }}>
            Upload an ERC roster XLSX file. The importer will auto-detect the ERC district from the filename
            and parse the wide A/B shift format.
          </Text>

          <div style={{
            border: '2px dashed #cbd5e1', borderRadius: 8, padding: 32, textAlign: 'center', cursor: 'pointer',
            background: '#f8fafc',
          }}>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              style={{ display: 'block', width: '100%', cursor: 'pointer' }}
            />
          </div>

          {importPreview && (
            <>
              <Alert color="green" icon={<IconCheck size={16} />}>
                Parsed {importPreview.records.length} personnel from {importPreview.erc} roster.
                {roster.filter(r => r.erc === importPreview.erc).length > 0 &&
                  ` This will replace ${roster.filter(r => r.erc === importPreview.erc).length} existing ${importPreview.erc} records.`
                }
              </Alert>

              <div style={{ overflowY: 'auto', maxHeight: 300, border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#f8fafc' }}>
                    <tr>
                      <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Name</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>ID</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Role</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Shift</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.records.slice(0, 50).map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '6px 10px' }}>{r.name}</td>
                        <td style={{ padding: '6px 10px' }}>{r.employeeId || '—'}</td>
                        <td style={{ padding: '6px 10px' }}>{r.role || '—'}</td>
                        <td style={{ padding: '6px 10px' }}>{r.shift}</td>
                      </tr>
                    ))}
                    {importPreview.records.length > 50 && (
                      <tr><td colSpan={4} style={{ padding: 8, textAlign: 'center', color: '#94a3b8' }}>
                        ... and {importPreview.records.length - 50} more
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <Group style={{ justifyContent: 'flex-end' }}>
                <Button variant="subtle" onClick={() => setImportPreview(null)}>Cancel</Button>
                <Button onClick={confirmImport} loading={importing}>
                  Import {importPreview.records.length} Records
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>
    </Stack>
  );
}
